package com.eladfinish.bible.contest.arch

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.eladfinish.bible.contest.common.classNameOf
import io.github.aakira.napier.Napier
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.Job
import kotlinx.coroutines.channels.BufferOverflow
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.FlowCollector
import kotlinx.coroutines.flow.MutableSharedFlow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asSharedFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.collectLatest
import kotlinx.coroutines.launch
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock
import kotlin.coroutines.CoroutineContext

abstract class ViewModelStore<STATE : StateKt, ACTION : ActionKt, EFFECT : EffectKt>(
    val tag: String,
    private val dispatchContext: CoroutineContext = Dispatchers.Default,
    private val effectDispatcher: CoroutineContext = Dispatchers.Default,
    val sideEffectFlow: MutableSharedFlow<EFFECT> = MutableSharedFlow(
        extraBufferCapacity = 1,
        onBufferOverflow = BufferOverflow.DROP_OLDEST
    ),
) : ViewModel(),
    Store<STATE, ACTION, EFFECT> {

    private val locker = Mutex()

    protected abstract val stateFlow: MutableStateFlow<STATE>

//    protected open val sideEffectFlow: MutableSharedFlow<EFFECT> = MutableSharedFlow<EFFECT>(
//        extraBufferCapacity = 1,
//        onBufferOverflow = BufferOverflow.DROP_OLDEST
//    )

//    protected open val sideEffectFlow = MutableSharedFlow<EFFECT>(
//        replay = 0,
//        extraBufferCapacity = 0,
//        onBufferOverflow = BufferOverflow.SUSPEND
//    )

    protected val currentState: STATE
        get() = stateFlow.value

    override fun observeState(): StateFlow<STATE> = stateFlow.asStateFlow()
    override fun observeSideEffect(): Flow<EFFECT> = sideEffectFlow.asSharedFlow()

    init {
        Napier.d(message = "${classNameOf(this::class)}.init", tag = tag)

        viewModelScope.launch(effectDispatcher) {
            sideEffectFlow.collect {
                Napier.d(message = "Dispatch SideEffect ${classNameOf(it::class)}", tag = tag)
            }
        }
    }

    /**
     * WARNING!
     * This function needs to return as quick as possible.
     * Any long term work should be handed off to a different thread
     **/
    override fun dispatch(action: ACTION) {
        Napier.d(message = "Dispatch Action ${classNameOf(action::class)} - $action", tag = tag)
        viewModelScope.launch(dispatchContext) {
            locker.withLock {
                val state = handleAction(action)
                emitStateInternal(state)
            }
        }
    }

    protected abstract fun handleAction(action: ACTION): STATE

    protected fun updateState(reducer: (STATE) -> STATE) {
        Napier.d(message = "Update State $reducer", tag = tag)
        viewModelScope.launch(dispatchContext) {
            locker.withLock {
                val newState = reducer(currentState)
                emitStateInternal(newState)
            }
        }
    }

    protected fun emitStateInternal(state: STATE) {
        Napier.d(message = "emitState state $state", tag = tag)
        stateFlow.value = state
    }

    protected open fun launchEffect(block: suspend CoroutineScope.() -> Unit): Job {
        Napier.d(message = "launchEffect", tag = tag)
        return viewModelScope.launch(
            context = effectDispatcher,
            block = block
        )
    }

    protected fun onlyEffect(effect: EFFECT): STATE {
        launchEffect {
            sideEffectFlow.emit(effect)
        }
        return currentState
    }

    protected open fun <T> Flow<T>.collectOnStoreLatest(
        action: suspend (value: T) -> Unit,
    ) {
        viewModelScope.launch(effectDispatcher) {
            collectLatest(action)
        }
    }

    protected open fun <T> Flow<T>.collectOnStore(
        collector: FlowCollector<T> = EmptyFlowCollector,
    ) {
        viewModelScope.launch(effectDispatcher) {
            collect(collector)
        }
    }

    protected open fun <T> FlowCollector<T>.emitOnStore(value: T) {
        viewModelScope.launch(effectDispatcher) {
            emit(value)
        }
    }
}
